import React from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';

// MUI Joy Components
import Card from '@mui/joy/Card';
import Typography from '@mui/joy/Typography';
import Chip, { ChipProps } from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import Divider from '@mui/joy/Divider';

// Material Icons
import Send from '@mui/icons-material/Send';
import MarkChatUnread from '@mui/icons-material/MarkChatUnread';
import Email from '@mui/icons-material/Email';
import ForwardToInbox from '@mui/icons-material/ForwardToInbox';
import Storefront from '@mui/icons-material/Storefront';
import PhoneCallback from '@mui/icons-material/PhoneCallback';
import PhoneForwarded from '@mui/icons-material/PhoneForwarded';
import RequestQuote from '@mui/icons-material/RequestQuote';
import Receipt from '@mui/icons-material/Receipt';
import Edit from '@mui/icons-material/Edit';
import PublishedWithChanges from '@mui/icons-material/PublishedWithChanges';
import FiberNew from '@mui/icons-material/FiberNew';
import EventAvailable from '@mui/icons-material/EventAvailable';
import SyncAlt from '@mui/icons-material/SyncAlt';
import HelpOutline from '@mui/icons-material/HelpOutline';


export const ACTION_CHOICES = [
    "No Actions Found", "Text Sent", "Text Received", "Email Sent", "Added To Email Chain",
    "Assigned To Franchise", "Incoming Call Missed", "Incoming Call Answered", "Outgoing Call Missed", "Outgoing Call Answered",
    "Roof Quote Sent", "Invoice Sent", "Customer Edited", "Edited", "Gutter Clean Cert Sent",
    "Status Changed", "Lead Created", "Booking Created", "Lead Converted To Booking", "Manual Klarna Invoice"
] as const;

export type ActionType = typeof ACTION_CHOICES[number];

export interface ActionLog {
    id: number;
    action: ActionType;
    context: string;
    lead: number;
    booking: number | null;
    created_at: string;
    user_name: string;
}

export interface ActionNodeData {
    action: ActionType;
    context: string;
    createdAt: string;
    userName: string;
}

// This function determines the color for the card, icon, etc.
export const getActionColor = (action: ActionType): ChipProps['color'] => {
    switch (action) {
        case 'Lead Created':
        case 'Booking Created':
        case 'Manual Klarna Invoice':
        case 'Lead Converted To Booking':
            return 'success';
        case 'Text Sent':
        case 'Email Sent':
        case 'Gutter Clean Cert Sent':
        case 'Roof Quote Sent':
        case 'Invoice Sent':
        case 'Outgoing Call Answered':
        case 'Incoming Call Answered':
            return 'primary';
        case 'Text Received':
            return 'neutral';
        case 'Status Changed':
        case 'Customer Edited':
        case 'Edited':
            return 'warning';
        case 'No Actions Found':
        case 'Outgoing Call Missed':
        case 'Incoming Call Missed':
            return "danger"
        case 'Assigned To Franchise':
        case 'Added To Email Chain':
            return 'neutral'; // Changed to info for better distinction
        default:
            return 'primary';
    }
};

// This new function maps an action to a Material Icon component
const getActionIcon = (action: ActionType): React.ElementType => {
    switch (action) {
        case 'Manual Klarna Invoice': return Send;
        case 'Text Sent': return Send;
        case 'Text Received': return MarkChatUnread;
        case 'Email Sent': return Email;
        case 'Added To Email Chain': return ForwardToInbox;
        case 'Assigned To Franchise': return Storefront;
        case 'Incoming Call Answered': return PhoneForwarded;
        case 'Outgoing Call Answered': return PhoneForwarded;
        case 'Outgoing Call Missed': return PhoneCallback;
        case 'Incoming Call Missed': return PhoneCallback;
        case 'Roof Quote Sent': return RequestQuote;
        case 'Gutter Clean Cert Sent': return RequestQuote;
        case 'Invoice Sent': return Receipt;
        case 'Customer Edited':
        case 'Edited':
            return Edit;
        case 'Status Changed': return PublishedWithChanges;
        case 'Lead Created': return FiberNew;
        case 'Booking Created': return EventAvailable;
        case 'Lead Converted To Booking': return SyncAlt;
        default: return HelpOutline;
    }
};


const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
    const minutes = Math.round(seconds / 60);
    const hours = Math.round(minutes / 60);
    const days = Math.round(hours / 24);
    const months = Math.round(days / 30.44);
    const years = Math.round(days / 365.25);

    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 30) return `${days}d ago`;
    if (months < 12) return `${months}mo ago`;
    return `${years}y ago`;
};

const CustomActionNode: React.FC<NodeProps<ActionNodeData>> = ({ data }) => {
    const color = getActionColor(data.action);
    const IconComponent = getActionIcon(data.action);
    const formattedDate = new Date(data.createdAt).toLocaleString();
    const timeAgo = formatTimeAgo(data.createdAt);

    return (
        <div>
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />

            <Card
                variant="outlined"
                sx={{
                    width: 320,
                    borderColor: `${color}.outlinedBorder`,
                    boxShadow: 'md',
                    gap: 0,
                    '&:hover': {
                        boxShadow: 'lg',
                        borderColor: `${color}.solidBg`,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out',
                    }
                }}
            >
                {/* Header: Icon and Action Name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconComponent sx={{ mt: 0.5, color: `${color}.solidBg` }} />
                    <Typography level="title-md">{data.action}</Typography>
                </Box>

                {data.userName && data.createdAt && (
                    <Box sx={{  pl: 4.5 /* Align with title text */ }}>
                        <Typography level="body-xs">
                            {formattedDate} ({timeAgo})
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            By: {data.userName}
                        </Typography>
                    </Box>
                )}

                {/* Divider and Context */}
                {data.context && (

                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Box
                                className="nodrag nowheel"
                                sx={{
                                    overflow: 'hidden',
                                    pr: 1,
                                }}
                            >
                                <Typography
                                    level="body-sm"
                                >
                                    {data.context}
                                </Typography>
                            </Box>
                        </>

                )}

            </Card>

            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
        </div>
    );
};

export default CustomActionNode;